// ============================================================================
// Intervention Graph — Node-based flow for each intervention
// ============================================================================
// Shared 14-node branching structure (task-3 → task-8 plus side pages A, A1,
// B, C, D, E, F, G, H, I, K, L, M, N) is generated from per-intervention
// "theme" copy in interventionThemes.js.
//
// Branch kinds:
//   { kind: "linear", next }
//   { kind: "distress-gate", threshold, onHigh, onLow }
//   { kind: "distress-question-gate", threshold, onHigh, question, onLowYes, onLowNo }
//   { kind: "end" }
// ============================================================================

import INTERVENTION_TASKS from "./interventionTasks";
import THEMES from "./interventionThemes";
import { getOverrideFor, deepMerge, subscribeContentOverrides } from "./contentOverrides";

// Linear graph builder — used only as a last-resort fallback.
function buildLinearGraph(tasks) {
  const nodes = {};
  tasks.forEach((task, idx) => {
    const taskNum = idx + 1;
    const id = `task-${taskNum}`;
    const nextId = taskNum < tasks.length ? `task-${taskNum + 1}` : null;
    nodes[id] = {
      title: task.title,
      content: task.content,
      taskNumber: taskNum,
      hasPlayer: Boolean(task.hasPlayer),
      hasDistressSlider: Boolean(task.hasPlayer) || taskNum === 9,
      distressPrompt: taskNum === 9
        ? "Final check: On a scale of 0-10, how much does this problem bother you now?"
        : (task.hasPlayer ? "How distressed do you feel now? (after bilateral stimulation)" : null),
      showNotImproving: taskNum === 3 || taskNum === 4,
      hasInput: Boolean(task.hasInput),
      inputPrompt: task.inputPrompt,
      celebration: task.celebration,
      branches: nextId ? { kind: "linear", next: nextId } : { kind: "end" },
    };
  });
  return { startNode: "task-1", nodes };
}

// ============================================================================
// BRANCHING GRAPH BUILDER
// ============================================================================
// Given the 9-task array (from interventionTasks.js) and a theme config
// (from interventionThemes.js), produce the full node graph with all 13
// branches wired up.
// ============================================================================
function buildBranchingGraph(srcTasks, t) {
  const task1 = srcTasks[0];
  const task2 = srcTasks[1];
  const task9 = srcTasks[8];
  const theme = t.theme; // e.g. "the stage fright"

  // -- helper: the distress prompt used on nearly every node --
  const distressPrompt = "Check your body and mind. Indicate your current level of distress on the scale.";

  return {
    startNode: "task-1",
    nodes: {
      // =========== TASK 1 — Getting Started ===========
      "task-1": {
        title: "Getting Started",
        content: task1.content,
        taskNumber: 1,
        branches: { kind: "linear", next: "task-1a" },
      },

      // =========== TASK 1A — What to expect (goal + slider explanation) ===========
      // Shares the same progress step (1/9) as task-1. Rendered with its own
      // editorial layout in InterventionFlow.jsx so it mirrors the task-1 feel.
      "task-1a": {
        title: "How we'll know it's working",
        taskNumber: 1,
        branches: { kind: "linear", next: "task-2" },
      },

      // =========== TASK 2 — Initial assessment ===========
      "task-2": {
        title: task2.title,
        content: task2.content,
        taskNumber: 2,
        hasInput: Boolean(task2.hasInput),
        inputPrompt: task2.inputPrompt,
        hasDistressSlider: true,
        distressPrompt: "On a scale of 0-10, how much does this problem bother you?",
        branches: { kind: "linear", next: "task-3" },
      },

      // =========== TASK 3 — Calming the body ===========
      "task-3": {
        title: "Calming Your Body",
        taskNumber: 3,
        content:
`${t.t3.opener ? t.t3.opener + "\n\n" : ""}First, let's focus on how your body responds to ${theme} and calm within your current body state. You might notice ${t.t3.bodyResponse}. ${t.t3.systemNote} You may also feel the emotional intensity — ${t.t3.emotionalDetail}.

For now, I'd like you to ignore the issue(s) leading to ${theme} and just think about your current physical and emotional state. Hold on to that awareness of your body's response. Start the bilateral sound and let your mind do the work. Just as a scratch on your skin heals by itself with no effort needed by you, your brain already knows what to do. Keep moving your eyes or tapping your legs as long as the sound is playing.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt:
`Check your body and mind. You might notice tension in your body (${t.t3.bodyTension}) or in your emotions (${t.t3.emotionalList}). Do you notice any body or mood discomforts? Indicate your current level of distress on the scale.`,
        branches: { kind: "distress-gate", threshold: 1, onHigh: "A", onLow: "task-4" },
      },
      "A": {
        title: "Keep Clearing",
        taskNumber: 3,
        content:
`You're noticing more of that familiar body and mood discomfort. Let's clear that out. This can take several repetitions.

Check your entire body and mind. Notice where the tension is. Now start the bilateral sound and notice how your mind and body respond.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        showNotImproving: true,
        branches: { kind: "distress-gate", threshold: 1, onHigh: "A1", onLow: "task-4" },
      },
      "A1": {
        title: "Shrinking the Tension",
        taskNumber: 3,
        content:
`Let's keep shrinking your body's tension. Check your entire body and mind. Notice where the distress is. Now start the bilateral sound and notice how your mind and body respond.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        showNotImproving: true,
        branches: { kind: "distress-gate", threshold: 1, onHigh: "A1", onLow: "task-4" },
      },

      // =========== TASK 4 — First pass through the story ===========
      "task-4": {
        title: "Facing the Story",
        taskNumber: 4,
        content:
`{{ENCOURAGE}} Now that your body feels calmer, let's turn your attention to ${t.t4.subject}. ${t.t4.storyIntro}

Start the bilateral sound and let ${t.t4.storyNoun} play through from beginning to end. Keep moving your eyes or tapping your legs while the sound plays. If you don't complete the story before the sound stops, don't worry. We'll address it in segments.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        showNotImproving: true,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "B",
          question: "Have you finished the story?",
          onLowYes: "task-5",
          onLowNo: "D",
        },
      },
      "B": {
        title: "Clearing Residual Tension",
        taskNumber: 4,
        content:
`There's that familiar body and mood tension again. Let's clear that out. Take a moment to gather up the story, tuck it into a quiet room in your mind, and close the door. Check your entire body and mind. Notice where the distress is. Now start the bilateral sound and notice how your mind and body respond.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        showNotImproving: true,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "C",
          question: "Have you finished the story?",
          onLowYes: "task-5",
          onLowNo: "D",
        },
      },
      "C": {
        title: "Dissolving More Tension",
        taskNumber: 4,
        content:
`Let's keep dissolving your body and mood tension. Check your entire body and mind. Notice where the distress is. Now start the bilateral sound and notice how your mind and body respond.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        showNotImproving: true,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "C",
          question: "Have you finished the story?",
          onLowYes: "task-5",
          onLowNo: "D",
        },
      },
      "D": {
        title: "Continuing the Story",
        taskNumber: 4,
        content:
`There's still a bit of the story to finish. No worries, most others have some too! Play the bilateral sounds and start the story in your mind right where you left off. We'll keep doing this until the whole story has played through. This is going to help you finally be rid of all that tension.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        showNotImproving: true,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "B",
          question: "Have you finished the story?",
          onLowYes: "task-5",
          onLowNo: "D",
        },
      },

      // =========== TASK 5 — Second pass ===========
      "task-5": {
        title: "Second Pass Through the Story",
        taskNumber: 5,
        content:
`{{ENCOURAGE}} Now, let's ${t.t5.gripPhrase}. Start the bilateral sound and ${t.t5.storyReplay}. Keep moving your eyes or tapping your legs while the sound plays. If you don't complete the story before the sound stops, we'll address it in segments just like before. ${t.t5.followup}`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "E",
          question: "Have you finished the story?",
          onLowYes: "task-6",
          onLowNo: "G",
        },
      },
      "E": {
        title: "Clearing Residual Tension",
        taskNumber: 5,
        content:
`There's that familiar body and mood tension again. Let's clear that out. Take a moment to gather up the story, tuck it back into a quiet room in your mind, and close the door. Check your entire body and mind. Notice where the distress is. Now start the bilateral sound and notice how your mind and body respond.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "F",
          question: "Have you finished the story?",
          onLowYes: "task-6",
          onLowNo: "G",
        },
      },
      "F": {
        title: "Washing Out the Leftover",
        taskNumber: 5,
        content:
`Let's keep washing out the leftover tension in your body and mood. Check your entire body and mind. Notice where the distress is. Now start the bilateral sound and notice how your mind and body respond.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "F",
          question: "Have you finished the story?",
          onLowYes: "task-6",
          onLowNo: "G",
        },
      },
      "G": {
        title: "Finishing the Story",
        taskNumber: 5,
        content:
`Your work is paying off! It looks like there's still some story left. Play the bilateral sounds and start the story in your mind right where you left off. We'll keep doing this until the whole story has played through. This is going to really help you up your game.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "E",
          question: "Have you finished the story?",
          onLowYes: "task-6",
          onLowNo: "G",
        },
      },

      // =========== TASK 6 — Positive story ===========
      "task-6": {
        title: "Creating a Deeper Calm",
        taskNumber: 6,
        content:
`{{ENCOURAGE}} ${t.t6.contextQuestion}

Now let's create a deeper calm. ${t.t6.positiveVision}

Start the bilateral sound and immerse yourself in this imagined calm, positive new story; play it from start to finish. Keep moving your eyes or tapping your legs while the sound plays.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "H",
          question: "Do you connect strongly with your positive story?",
          onLowYes: "task-7",
          onLowNo: "I",
        },
      },
      "H": {
        title: "Strengthening the Positive Story",
        taskNumber: 6,
        content:
`Your positive story is likely getting stronger now. Let's stay with it and make it as powerful as possible. Check your entire body and mind. Notice anywhere it feels positive and uplifting. Now start the bilateral sound and notice how your mind and body respond.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "I",
          question: "Do you connect strongly with your positive story?",
          onLowYes: "task-7",
          onLowNo: "I",
        },
      },
      "I": {
        title: "Deepening the Connection",
        taskNumber: 6,
        content:
`Permit yourself to experience your positive story as deeply as you can! Let yourself absorb the strength, calm, freedom, joy, or any other feeling that comes with it.

If you find your positive story doesn't seem to fit well, swap it out with another, better story! Many people need to try on a few positive stories. Now start the bilateral sound and notice how your mind and body respond.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "H",
          question: "Do you connect strongly with your positive story?",
          onLowYes: "task-7",
          onLowNo: "I",
        },
      },

      // =========== TASK 7 — Overlay ===========
      "task-7": {
        title: "Replacing the Old Story",
        taskNumber: 7,
        content:
`{{ENCOURAGE}} Okay, take your new positive, powerful image and place it right over the old version — covering it completely, ${t.t7.metaphor}. ${t.t7.transformations}

Start the bilateral sound and stay with it — keep your eyes moving or tap along while it plays. Just let your mind and body settle into this new feeling.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "K",
          question: "Is the old story gone?",
          onLowYes: "task-8",
          onLowNo: "L",
        },
      },
      "K": {
        title: "Letting the New Story Take Over",
        taskNumber: 7,
        content:
`Stay with it. See yourself completely cover the old story with the new one. Feel the new strength, calm, and confidence completely take over.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "L",
          question: "Is the old story gone?",
          onLowYes: "task-8",
          onLowNo: "L",
        },
      },
      "L": {
        title: "Settling Into the Change",
        taskNumber: 7,
        content:
`Stay with it and feel your body and mind change with you.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "L",
          question: "Is the old story gone?",
          onLowYes: "task-8",
          onLowNo: "L",
        },
      },

      // =========== TASK 8 — Future rehearsal ===========
      "task-8": {
        title: "Carrying It Into the Future",
        taskNumber: 8,
        content:
`{{ENCOURAGE}} Let's help you carry this positive change into the future. Take a moment and think about the things that used to trigger you — ${t.t8.triggerExamples}. There are probably other triggers too!

Now, imagine going forward into the future and running into those same situations. Do the old triggers still carry the same weight?

Start the bilateral sound and picture yourself moving through these situations with calm, purpose, and confidence. Keep moving your eyes or tapping your legs while the sound plays.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "M",
          question: "Can you see the future with confidence and total calm?",
          onLowYes: "task-9",
          onLowNo: "N",
        },
      },
      "M": {
        title: "Relaxing Into the Future",
        taskNumber: 8,
        content:
`Stay with it and feel your body and mind relax as they change with you.`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "N",
          question: "Can you see the future with confidence and total calm?",
          onLowYes: "task-9",
          onLowNo: "N",
        },
      },
      "N": {
        title: "Growing Calmer and More Grounded",
        taskNumber: 8,
        content:
`Stay with it and feel your body and mind change with you. Feel yourself growing calmer and more grounded!`,
        hasPlayer: true,
        hasDistressSlider: true,
        distressPrompt,
        branches: {
          kind: "distress-question-gate",
          threshold: 1,
          onHigh: "N",
          question: "Can you see the future with confidence and total calm?",
          onLowYes: "task-9",
          onLowNo: "N",
        },
      },

      // =========== TASK 9 — Completion ===========
      "task-9": {
        title: task9.title,
        content: task9.content,
        celebration: task9.celebration,
        taskNumber: 9,
        hasInput: Boolean(task9.hasInput),
        inputPrompt: task9.inputPrompt,
        hasDistressSlider: true,
        distressPrompt: "Final check: On a scale of 0-10, how much does this problem bother you now?",
        branches: { kind: "end" },
      },
    },
  };
}

// ============================================================================
// Relief From Grief overrides
// ============================================================================
function buildReliefFromGriefOverrides() {
  return {
    "task-3": {
      content:
`You've taken an important and brave first step!

To prepare for this important work, let's focus on how your body responds to the grief and on calming your current body state. You might notice a heaviness in your chest, a lump in your throat, tears pressing behind your eyes, or a hollow space that never quite fills. Grief lives in the body — it comes in waves, often arriving without warning. You may also feel the emotional intensity — the longing, the sorrow, or the ache of missing someone or something that was here and isn't now.

For now, I'd like you to ignore the issue(s) leading to the grief and just think about your current physical and emotional state. Hold on to that awareness of your body's response. Start the bilateral sound and let your mind do the work. Like a scratch on your skin healing by itself with no effort needed by you, your brain already knows what to do. Keep moving your eyes or tapping your legs as long as the sound is playing.`,
    },
    "task-5": {
      content:
`{{ENCOURAGE}} Now, let's further soften the edges of your grief. Any positive memories or happiness underneath the grief will be untouched. Start the bilateral sound and let the grief story move from start to finish again. Keep moving your eyes or tapping your legs while the sound plays. If you don't complete the story before the sound stops, we'll address it in segments just like before. At this point, the grief may feel a little lighter, with more love and less sting this time.`,
    },
    "task-8": {
      content:
`{{ENCOURAGE}} Let's help you carry this positive change into the future. Take a moment and think about the things that used to trigger you related to your grief— a song that brings them back, an anniversary, a place you used to go together, a quiet evening when the loss felt loud. There are probably other triggers too!

Now, imagine going forward into the future and running into those same situations. Do the old triggers still carry the same weight?

Start the bilateral sound and picture yourself moving through these situations with calm, purpose, and confidence. Keep moving your eyes or tapping your legs while the sound plays.`,
    },
  };
}

// ============================================================================
// Public API
// ============================================================================
const CACHE = {};

// When admin updates content overrides, drop the graph cache so the next
// caller rebuilds with the new copy. Subscribed once at module load.
subscribeContentOverrides(() => {
  for (const key of Object.keys(CACHE)) delete CACHE[key];
});

function applyOverrides(type, tasks, theme) {
  const override = getOverrideFor(type);
  let nextTasks = tasks;
  let nextTheme = theme;
  if (override.tasks && override.tasks.length) {
    nextTasks = tasks.map((task, idx) => {
      const patch = override.tasks.find((t) => t.index === idx);
      if (!patch) return task;
      const { index: _ignored, ...rest } = patch;
      return { ...task, ...rest };
    });
  }
  if (override.theme && Object.keys(override.theme).length) {
    nextTheme = deepMerge(theme, override.theme);
  }
  return { tasks: nextTasks, theme: nextTheme };
}

export function getInterventionGraph(type) {
  if (CACHE[type]) return CACHE[type];
  const baseTasks = INTERVENTION_TASKS[type];
  const baseTheme = THEMES[type];
  const merged = baseTasks && baseTheme
    ? applyOverrides(type, baseTasks, baseTheme)
    : { tasks: baseTasks, theme: baseTheme };
  const tasks = merged.tasks;
  const theme = merged.theme;
  let graph;
  if (tasks && theme) {
    graph = buildBranchingGraph(tasks, theme);
    if (type === "relief_from_grief") {
      // Grief-specific wordsmithing on top of the template.
      const overrides = buildReliefFromGriefOverrides();
      Object.entries(overrides).forEach(([nodeId, patch]) => {
        if (graph.nodes[nodeId]) {
          graph.nodes[nodeId] = { ...graph.nodes[nodeId], ...patch };
        }
      });
    }
    if (type === "nightmare_reset") {
      // Nightmare-specific page titles + content — task 6 reframes the
      // dream into a new story, task 7 then overlays it on top of the old
      // dream. Both nodes get nightmare-specific wording instead of the
      // generic templates used for other practices.
      if (graph.nodes["task-6"]) {
        graph.nodes["task-6"] = {
          ...graph.nodes["task-6"],
          title: "Rewriting the Story",
        };
      }
      if (graph.nodes["task-7"]) {
        graph.nodes["task-7"] = {
          ...graph.nodes["task-7"],
          title: "Replacing the Old Dream",
          content:
`Super! Look how far you've come. Okay, take your new positive, powerful dream story and place it right over the old version of the dream — covering it completely, just like replacing old stained carpet with a beautiful, pristine new wood floor. Every dark corner fills with light, every threat shrinks down to size, every helpless moment becomes one where you have exactly what you need.

Start the bilateral sound and stay with it — keep your eyes moving or tap along while it plays. Just let your mind and body settle into this new feeling.`,
        };
      }
    }
  } else if (tasks) {
    graph = buildLinearGraph(tasks);
  } else {
    graph = null;
  }
  CACHE[type] = graph;
  return graph;
}

// Helper to resolve the next node from a node's branch rules
export function resolveNextNode(node, { sud, answerYes }) {
  const b = node.branches;
  if (!b) return null;
  switch (b.kind) {
    case "linear":
      return b.next;
    case "distress-gate":
      return sud > b.threshold ? b.onHigh : b.onLow;
    case "distress-question-gate":
      if (sud > b.threshold) return b.onHigh;
      return answerYes ? b.onLowYes : b.onLowNo;
    case "end":
    default:
      return null;
  }
}
